import apiClient from './apiClient';
import type {
  DirectShearReading,
  ReadingsByStage,
  ParsedStageData,
} from '../types/directShearReading';

/** Map camelCase parser output to PascalCase for the backend */
const mapReadingsToPascal = (data: ParsedStageData) => ({
  stageNumber: data.stageNumber,
  readings: data.readings.map((r) => ({
    SampleInt: r.sampleInt,
    HorizontalDisplacement: r.horizontalDisplacement,
    HorizontalLoad: r.horizontalLoad,
    VerticalDisplacement: r.verticalDisplacement,
    VerticalLoad: r.verticalLoad,
    IsSeating: r.isSeating === true,
  })),
});

export const getReadings = async (
  generalKey: string,
): Promise<ReadingsByStage> => {
  const response = await apiClient.get<ReadingsByStage>(
    `/direct-shear/${generalKey}/readings`,
  );
  return response.data;
};

export const uploadStageReadings = async (
  generalKey: string,
  data: ParsedStageData,
): Promise<{ count: number }> => {
  const response = await apiClient.post<{ count: number }>(
    `/direct-shear/${generalKey}/readings/upload`,
    mapReadingsToPascal(data),
  );
  return response.data;
};

export const replaceStageReadings = async (
  generalKey: string,
  stageNumber: number,
  data: ParsedStageData,
): Promise<{ deleted: number; created: number }> => {
  const response = await apiClient.put<{ deleted: number; created: number }>(
    `/direct-shear/${generalKey}/stages/${stageNumber}/readings`,
    { readings: mapReadingsToPascal(data).readings },
  );
  return response.data;
};

export const addReading = async (
  generalKey: string,
  stageNumber: number,
  reading: Partial<DirectShearReading>,
): Promise<DirectShearReading> => {
  const response = await apiClient.post<DirectShearReading>(
    `/direct-shear/${generalKey}/stages/${stageNumber}/readings`,
    reading,
  );
  return response.data;
};

export const updateReading = async (
  readingKey: string,
  data: Partial<DirectShearReading>,
): Promise<DirectShearReading> => {
  const response = await apiClient.put<DirectShearReading>(
    `/direct-shear/readings/${readingKey}`,
    data,
  );
  return response.data;
};

export const deleteReading = async (readingKey: string): Promise<void> => {
  await apiClient.delete(`/direct-shear/readings/${readingKey}`);
};

export const bulkDeleteReadings = async (
  keys: string[],
): Promise<{ deleted: number }> => {
  const response = await apiClient.post<{ deleted: number }>(
    '/direct-shear/readings/bulk-delete',
    { keys },
  );
  return response.data;
};

export const calculateStage = async (
  generalKey: string,
  stageNumber: number,
  dilationRange: number = 0.375,
  confirmReset = false,
): Promise<{ rowsCalculated: number }> => {
  const response = await apiClient.post<{ rowsCalculated: number }>(
    `/direct-shear/${generalKey}/stages/${stageNumber}/calculate`,
    { dilationRange, confirmReset },
  );
  return response.data;
};
